import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, SpeakerHigh, SpeakerLow, SpeakerNone, X, Repeat, Playlist, Crown } from '@phosphor-icons/react';
import { usePlayer, useAuth, API } from '../App';
import { ShareMenu } from './ShareMenu';
import { trackUrl, singleUrl } from '../utils/slugUrls';
import { fileUrl, IMG_WIDTHS } from '../utils/imageUrl';
import { useAddToPlaylist } from '../contexts/AddToPlaylistContext';
import axios from 'axios';

const PLACEHOLDER_COVER = "https://images.unsplash.com/photo-1603731535872-6bbe81cc5f50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxibGFjayUyMG1hbGUlMjBzaW5nZXIlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzc1MjMxNjY1fDA&ixlib=rb-4.1.0&q=60&w=200";

const PREVIEW_LIMIT_SECONDS = 30;
const FADE_DURATION = 3;

function formatQuotaRemaining(seconds) {
  const s = Math.max(0, Math.round(seconds));
  if (s >= 60) return `${Math.ceil(s / 60)} min`;
  return `${s}s`;
}

export function MusicPlayer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    currentTrack, isPlaying, isPreviewMode, fullTrackList,
    togglePlayPause, playNext, playPrevious, stopPlaying, setIsPlaying,
    consumeQuota, dailyLimitReached, quotaLimitSeconds, remainingQuotaSeconds, refreshQuota,
  } = usePlayer();
  const audioRef = useRef(null);
  const streamStartRef = useRef(0);
  const lastLoggedTrackRef = useRef(null);
  const previewCtaShownRef = useRef(false);  // session-scoped: show subscribe CTA at most once
  const limitCtaShownRef = useRef(false);    // session-scoped: show quota-exhausted CTA at most once
  const lastQuotaTickRef = useRef(0);        // last currentTime we deducted from quota
  const fadeIntervalRef = useRef(null);
  const advancingRef = useRef(false); // Guard against double-fire of handleEnded
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);

  const isPremium = user?.subscription_tier?.startsWith('premium');
  const isGuest = !user;
  // Loop indicator: shown for albums (>1 tracks) AND singles (1-track playback now also loops indefinitely).
  const isLooping = Boolean(currentTrack);
  const { openForTrack: openPlaylistModal, isPremium: canAddToPlaylist } = useAddToPlaylist() || {};

  const logStream = (trackId, durationSec) => {
    if (!trackId || durationSec < 5) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const loc = navigator.language || '';
    axios.post(`${API}/streams/log`, { track_id: trackId, duration_seconds: Math.round(durationSec), timezone: tz, locale: loc }).catch(() => {});
  };

  // Clear any active fade interval
  const clearFade = useCallback(() => {
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  }, []);

  // Restore volume to user-set level (after fade or track switch)
  const restoreVolume = useCallback(() => {
    clearFade();
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume, clearFade]);

  useEffect(() => {
    if (audioRef.current && currentTrack) {
      // Reset the advancing guard for the new track
      advancingRef.current = false;
      // Log previous track stream before switching
      if (lastLoggedTrackRef.current && lastLoggedTrackRef.current !== currentTrack.track_id && streamStartRef.current > 0) {
        logStream(lastLoggedTrackRef.current, audioRef.current.currentTime);
      }
      lastLoggedTrackRef.current = currentTrack.track_id;
      streamStartRef.current = Date.now();
      lastQuotaTickRef.current = 0;
      // Use the DB duration as initial value (mutagen-extracted), audio element may report Infinity for streams
      if (currentTrack.duration && currentTrack.duration > 0) {
        setDuration(currentTrack.duration);
      } else {
        setDuration(0);
      }
      restoreVolume();
      const streamUrl = `${API}/tracks/${currentTrack.track_id}/stream?preview=${isPreviewMode ? 'true' : 'false'}`;
      const audio = audioRef.current;
      audio.src = streamUrl;
      audio.load();

      // Use canplay event for reliable autoplay (especially on iOS)
      if (isPlaying) {
        const onCanPlay = () => {
          audio.play().catch(() => {});
          audio.removeEventListener('canplay', onCanPlay);
        };
        audio.addEventListener('canplay', onCanPlay);
        // Also try immediate play (works on desktop and when audio is already unlocked on iOS)
        audio.play().catch(() => {});
      }
    }
  }, [currentTrack, isPreviewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Keep audio volume in sync with slider (only when not fading)
  useEffect(() => {
    if (audioRef.current && !fadeIntervalRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Start the 3-second fade-out toward the end of the track
  const startFadeOut = useCallback((endTime) => {
    if (fadeIntervalRef.current || !audioRef.current) return;
    const startVol = audioRef.current.volume;
    const step = 50; // ms between ticks
    fadeIntervalRef.current = setInterval(() => {
      if (!audioRef.current) { clearFade(); return; }
      const remaining = endTime - audioRef.current.currentTime;
      if (remaining <= 0) {
        audioRef.current.volume = 0;
        clearFade();
        return;
      }
      audioRef.current.volume = Math.max(0, startVol * (remaining / FADE_DURATION));
    }, step);
  }, [clearFade]);

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const time = audioRef.current.currentTime;
    setCurrentTime(time);

    // Determine the effective end point
    const effectiveEnd = (isPreviewMode && !isPremium) ? PREVIEW_LIMIT_SECONDS : duration;

    // Consume daily quota — only when playing full-length (not preview) and
    // we're a signed-in non-premium fan. Deduct the *delta* since the last
    // tick so we're not double-counting.
    if (!isPreviewMode && !isPremium && !isGuest && time > lastQuotaTickRef.current) {
      const delta = time - lastQuotaTickRef.current;
      if (delta > 0 && delta < 5) consumeQuota(delta); // ignore jumps from seeks
      lastQuotaTickRef.current = time;
    }

    // Start fade-out 3s before end
    if (effectiveEnd > FADE_DURATION && time >= effectiveEnd - FADE_DURATION && !fadeIntervalRef.current) {
      startFadeOut(effectiveEnd);
    }

    // Enforce 30s preview limit for non-premium
    if (isPreviewMode && !isPremium && time >= PREVIEW_LIMIT_SECONDS) {
      if (advancingRef.current) return; // Already advancing
      advancingRef.current = true;
      audioRef.current.pause();
      clearFade();
      // Show the appropriate CTA:
      //  • Guests → "Sign in to play full tracks" (routes to /login)
      //  • Signed-in fans who exhausted the daily 15-min budget → "You've
      //    reached your daily full-playback limit." (routes to /pricing)
      //  • Signed-in fans hitting a preview cap on a marketing track → the
      //    legacy sample CTA still applies.
      const showLimitReached = !isGuest && dailyLimitReached;
      const showGuestCta = isGuest;
      const shouldShow = (showGuestCta || showLimitReached) && !previewCtaShownRef.current && !limitCtaShownRef.current;
      if (shouldShow) {
        if (showLimitReached) limitCtaShownRef.current = true;
        else previewCtaShownRef.current = true;
        import('sonner').then(({ toast }) => {
          toast.custom((t) => (
            <div
              className="rounded-xl p-4 shadow-2xl border border-[#F59E0B]/30 bg-[#141417]/95 backdrop-blur-md max-w-sm w-full"
              data-testid={showLimitReached ? 'daily-limit-cta' : 'guest-signin-cta'}
            >
              <div className="flex items-start gap-3">
                <Crown weight="fill" className="text-[#F59E0B] text-2xl flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">
                    {showLimitReached ? 'Daily limit reached' : 'Sign in to play full tracks'}
                  </p>
                  <p className="text-white/60 text-xs mt-1 leading-relaxed">
                    {showLimitReached
                      ? `You've reached your daily full-playback limit. Subscribe to unlock full-length streaming and support the artists you love.`
                      : `You're hearing a 30-second sample. Sign in — free — to enjoy longer plays and support the artists you love.`}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => {
                        toast.dismiss(t);
                        navigate(showLimitReached ? '/pricing' : '/login');
                      }}
                      className="bg-[#F59E0B] text-black text-xs font-bold px-4 py-1.5 rounded-full hover:bg-[#D97706] transition-colors"
                      data-testid={showLimitReached ? 'daily-limit-cta-upgrade' : 'guest-signin-cta-signin'}
                    >
                      {showLimitReached ? 'View Plans' : 'Sign in'}
                    </button>
                    <button
                      onClick={() => toast.dismiss(t)}
                      className="text-white/50 text-xs hover:text-white px-3 py-1.5"
                      data-testid="preview-cta-not-now"
                    >
                      Not now
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => toast.dismiss(t)}
                  className="text-white/40 hover:text-white ml-1 flex-shrink-0"
                  aria-label="Close"
                  data-testid="preview-cta-close"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ), { duration: Infinity });
        });
      }
      // Log stream then advance to next track (preserves album loop)
      if (currentTrack) logStream(currentTrack.track_id, PREVIEW_LIMIT_SECONDS);
      restoreVolume();
      playNext();
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const d = audioRef.current.duration;
      if (d && isFinite(d)) setDuration(d);
    }
  };

  const handleEnded = () => {
    // Guard against double-fire (iOS can fire ended + pause in quick succession)
    if (advancingRef.current) return;
    advancingRef.current = true;
    if (currentTrack) logStream(currentTrack.track_id, audioRef.current?.currentTime || 0);
    clearFade();
    restoreVolume();
    playNext();
  };

  // iOS Safari can stall on buffering — detect and recover
  const handleStalled = () => {
    if (audioRef.current && isPlaying && !advancingRef.current) {
      // Retry play after a brief pause
      setTimeout(() => {
        if (audioRef.current && isPlaying) {
          audioRef.current.play().catch(() => {});
        }
      }, 500);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      let seekTo = percent * duration;
      if (isPreviewMode && !isPremium && seekTo > PREVIEW_LIMIT_SECONDS) seekTo = PREVIEW_LIMIT_SECONDS;
      // If seeking before the fade zone, restore volume
      const effectiveEnd = (isPreviewMode && !isPremium) ? PREVIEW_LIMIT_SECONDS : duration;
      if (seekTo < effectiveEnd - FADE_DURATION) restoreVolume();
      audioRef.current.currentTime = seekTo;
    }
  };

  // Cleanup fade interval on unmount
  useEffect(() => () => clearFade(), [clearFade]);

  const formatTime = (time) => {
    if (!time || isNaN(time) || !isFinite(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getImageUrl = (path) => fileUrl(path, IMG_WIDTHS.PLAYER_THUMB) || PLACEHOLDER_COVER;

  const VolumeIcon = volume === 0 ? SpeakerNone : volume < 0.5 ? SpeakerLow : SpeakerHigh;

  if (!currentTrack) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 h-24 glass border-t border-white/10 z-50" data-testid="music-player">
      <audio
        ref={audioRef}
        playsInline
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onStalled={handleStalled}
        onWaiting={handleStalled}
      />
      
      <div className="h-full max-w-screen-2xl mx-auto px-6 flex items-center gap-6">
        {/* Track Info */}
        <div className="flex items-center gap-4 w-64 flex-shrink-0">
          <Link
            to={currentTrack.is_single ? singleUrl(currentTrack) : trackUrl(currentTrack)}
            className="w-14 h-14 rounded-lg overflow-hidden shadow-lg flex-shrink-0 hover:ring-2 hover:ring-[#F59E0B]/60 hover:ring-offset-2 hover:ring-offset-[#0A0A0B] transition-all"
            title="Open detail page"
            aria-label={`Open detail page for ${currentTrack.title}`}
            data-testid="player-thumbnail-link"
          >
            <img
              src={getImageUrl(currentTrack.cover_art)}
              alt={currentTrack.title}
              className="w-full h-full object-cover"
            />
          </Link>
          <Link
            to={currentTrack.is_single ? singleUrl(currentTrack) : trackUrl(currentTrack)}
            className="min-w-0 group"
            data-testid="player-title-link"
          >
            <p className="text-white font-medium truncate group-hover:underline decoration-[#F59E0B]/60">{currentTrack.title}</p>
            <p className="text-sm text-white/60 truncate">{currentTrack.artist_name}</p>
          </Link>
          {isPreviewMode && !isPremium && (
            <span className="text-xs text-[#F59E0B] px-2 py-1 rounded bg-[#F59E0B]/10 flex-shrink-0" data-testid="preview-badge">
              30s Preview
            </span>
          )}
          {!isPreviewMode && !isPremium && !isGuest && remainingQuotaSeconds !== null && quotaLimitSeconds && (
            <span
              className={`text-xs px-2 py-1 rounded flex-shrink-0 whitespace-nowrap ${
                remainingQuotaSeconds <= 60
                  ? 'text-red-300 bg-red-500/10'
                  : remainingQuotaSeconds <= 300
                  ? 'text-amber-300 bg-amber-500/10'
                  : 'text-white/60 bg-white/5'
              }`}
              data-testid="quota-remaining-badge"
              title={`Free-tier daily budget: ${Math.floor(quotaLimitSeconds / 60)} min/day. Upgrade for unlimited listening.`}
            >
              {formatQuotaRemaining(remainingQuotaSeconds)} left today
            </span>
          )}
        </div>

        {/* Controls */}
        <div className="flex-1 flex flex-col items-center">
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={playPrevious}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="prev-btn"
            >
              <SkipBack weight="fill" className="text-xl" />
            </button>
            
            <button
              onClick={togglePlayPause}
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform"
              data-testid="play-pause-btn"
            >
              {isPlaying ? (
                <Pause weight="fill" className="text-black text-xl" />
              ) : (
                <Play weight="fill" className="text-black text-xl ml-0.5" />
              )}
            </button>
            
            <button
              onClick={playNext}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="next-btn"
            >
              <SkipForward weight="fill" className="text-xl" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="w-full max-w-xl flex items-center gap-3">
            <span className="text-xs text-white/60 w-10 text-right">{formatTime(currentTime)}</span>
            <div
              className="flex-1 h-1 bg-white/20 rounded-full cursor-pointer group relative"
              onClick={handleSeek}
            >
              {isPreviewMode && !isPremium && duration > PREVIEW_LIMIT_SECONDS && (
                <div
                  className="absolute top-0 h-full bg-white/10 rounded-full pointer-events-none"
                  style={{ width: `${(PREVIEW_LIMIT_SECONDS / duration) * 100}%` }}
                />
              )}
              <div
                className="h-full bg-[#F59E0B] rounded-full relative group-hover:bg-[#D97706] transition-colors"
                style={{ width: `${duration ? (currentTime / (isPreviewMode && !isPremium ? Math.min(duration, PREVIEW_LIMIT_SECONDS) : duration)) * 100 : 0}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <span className="text-xs text-white/60 w-10">
              {isPreviewMode && !isPremium ? formatTime(Math.min(duration, PREVIEW_LIMIT_SECONDS)) : formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Share, Volume, Loop Indicator & Close */}
        <div className="flex items-center gap-4 w-64 justify-end">
          <ShareMenu
            url={`${window.location.origin}${currentTrack.is_single ? singleUrl(currentTrack) : trackUrl(currentTrack)}`}
            ogUrl={`${API}/og/${currentTrack.is_single ? 'single' : 'track'}/${currentTrack.artist_slug || ''}/${currentTrack.slug || ''}`}
            title={`${currentTrack.title} by ${currentTrack.artist_name || 'Unknown Artist'} - Gospeleon`}
          />
          {canAddToPlaylist && (
            <button
              onClick={() => openPlaylistModal(currentTrack.track_id)}
              className="text-white/40 hover:text-[#F59E0B] transition-colors"
              title="Add to Playlist"
              data-testid="player-add-to-playlist"
            >
              <Playlist className="w-5 h-5" />
            </button>
          )}
          {isLooping && (
            <span className="text-[#F59E0B] flex items-center gap-1 text-xs" data-testid="loop-indicator" title={fullTrackList.length > 1 ? 'Album loop' : 'Single on repeat'}>
              <Repeat className="w-4 h-4" />
            </span>
          )}
          <div className="flex items-center gap-2">
            <VolumeIcon className="text-white/60" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-20"
              data-testid="volume-slider"
            />
          </div>
          <button
            onClick={() => {
              if (currentTrack) logStream(currentTrack.track_id, audioRef.current?.currentTime || 0);
              clearFade();
              stopPlaying();
            }}
            className="text-white/40 hover:text-white transition-colors"
            data-testid="close-player-btn"
          >
            <X className="text-xl" />
          </button>
        </div>
      </div>
    </div>
  );
}
